/**
 * Sale report column
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
/**
 * Redux
 */
import { useSelector } from 'react-redux';
import { wizardState } from '../redux/wizardSlice';
import { lookupsState } from '../redux/lookupsSlice';
import { leadState } from '../redux/leadSlice';

/**
 * Helpers
 */
import formatters from '../helpers/formatters';

/**
 * Icons
 */
import { Headphones, Chat } from '../components/SVGIcons';
import VehicleName from '../components/VehicleName';

const useStyles = makeStyles((theme) => ({
  sidebarPanel: {
    [theme.breakpoints.up('md')]: {
      marginRight: 20,
    },
  },
  sidebarPanelTop: {
    display: 'none',
    borderRadius: 16,
    marginBottom: 20,
    padding: '48px 22px 29px',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.secondary.main,
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  sidebarPanelBottom: {
    borderRadius: 20,
    padding: 20,
    boxSizing: 'border-box',
    [theme.breakpoints.up('md')]: {
      borderRadius: 16,
    },
  },
  evidence: {
    fontSize: '1.4em',
    lineHeight: '1.3em',
    fontWeight: 'bold',
  },
  flexContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  contact: {
    fontWeight: 'bold',
    outline: 'none',
    margin: '0 10px',
    textDecoration: 'none',
    '& > svg': {
      marginBottom: 10,
      height: 36,
    },
    '&:hover, &:active, &:focus': {
      textDecoration: 'none',
    },
  },
  services: {
    borderColor: '#bfbfbf',
  },
  service: {
    borderColor: '#bfbfbf',
  },
}));

/**
 * @returns {JSX.Element}
 */
function Sidebar() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { step } = useSelector(wizardState);
  const { selectedServices } = useSelector(leadState);
  const { configuration } = useSelector(lookupsState);
  const {
    image, makeLogo, price,
  } = configuration.vehicle || {};
  const { email, phone } = configuration.contact ?? { email: '', phone: '' };
  const totalServices = selectedServices
    .map((item) => item.value).reduce((prev, curr) => prev + parseInt(curr, 10), 0);

  const showMedia = step < 2 || selectedServices.length === 0;
  const showMakeLogo = showMedia && makeLogo !== 'hidden';

  return (
    <div className={`${classes.sidebarPanel}`}>
      <div className={`ga-ecommerce-sidebar-top ${classes.sidebarPanelTop}`}>
        { showMedia && <img src={image} alt="" style={{ marginBottom: 20 }} /> }
        <div
          style={{
            display: 'flex',
            justifyContent: showMedia ? 'flex-start' : 'space-between',
            marginBottom: showMedia ? 75 : 35,
          }}
        >
          { showMakeLogo && <img src={makeLogo} alt="" style={{ height: 48, marginRight: 20 }} /> }
          <VehicleName />
          { !showMedia && <div style={{ fontWeight: 'bold', marginLeft: 20 }}>{`${formatters.number(price)} €`}</div> }
        </div>
        { !showMedia
          && (
            <div className={classes.services} style={{ marginBottom: 75, borderTop: '1px solid' }}>
              {
                selectedServices.map((service) => (
                  <div
                    className={`${classes.service} ${classes.flexContainer}`}
                    style={{ borderBottom: '1px solid', height: 45, lineHeight: '45px' }}
                    key={service.id}
                  >
                    <div style={{ marginRight: 20 }}>
                      <strong>{`${service.serviceName} - `}</strong>
                      <span>{service.name}</span>
                    </div>
                    <div>
                      {`${formatters.number(service.value)[0] !== '-' ? '+' : ''}${formatters.number(service.value)} €`}
                    </div>
                  </div>
                ))
              }
            </div>
          )}
        <div className={`${classes.evidence} ${classes.flexContainer}`}>
          <div style={{ marginRight: 20, textTransform: 'uppercase' }}>{t('total')}</div>
          <div>{`${formatters.thousand(price + totalServices)} €`}</div>
        </div>
      </div>
      <div className={`ga-ecommerce-sidebar-bottom ${classes.sidebarPanelBottom}`} style={{ textAlign: 'center' }}>
        <div className={classes.evidence} style={{ marginBottom: 20 }}>{t('Contact us for support or information')}</div>
        <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <a href={`mailto:${email}`} className={classes.contact}>
            <Headphones />
            <br />
            {email}
          </a>
          <a href={`tel:${phone}`} className={classes.contact}>
            <Chat />
            <br />
            {phone}
          </a>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
